import moment from 'moment';

import { MailAiTone, MailCategory, MailRewriteMode } from 'core/graphql';

/**
 * ТЗ 0122, «ИИ в почте» — справочники и чистые правила: категории,
 * приоритеты, тоны ответа, режимы «улучшить текст», факты «Кто пишет».
 */

export const MAIL_CATEGORIES: MailCategory[] = [
  MailCategory.Study,
  MailCategory.Payment,
  MailCategory.Funding,
  MailCategory.Sales,
  MailCategory.Contract,
  MailCategory.B2B,
  MailCategory.Accounting,
  MailCategory.Complaint,
  MailCategory.Tech,
  MailCategory.Hr,
  MailCategory.Promo,
  MailCategory.Other,
];

/** Ключ перевода категории: `mail.ai.categories.<key>`. */
export const categoryKey = (category?: string | null): string =>
  (category || 'OTHER').toLowerCase();

export const isMailCategory = (value?: string | null): value is MailCategory =>
  (MAIL_CATEGORIES as string[]).includes((value || '').toUpperCase());

/** Чип приоритета — только у срочных и важных (§D). */
export const priorityChip = (
  priority?: string | null,
): 'urgent' | 'high' | null => {
  const value = (priority || '').toLowerCase();
  return value === 'urgent' || value === 'high' ? value : null;
};

export type MailToneKey =
  | 'default'
  | 'short'
  | 'full'
  | 'formal'
  | 'friendly'
  | 'followup';

export const MAIL_TONES: MailToneKey[] = [
  'default',
  'short',
  'full',
  'formal',
  'friendly',
  'followup',
];

const TONE_ENUM: Record<MailToneKey, MailAiTone> = {
  default: MailAiTone.Default,
  short: MailAiTone.Short,
  full: MailAiTone.Full,
  formal: MailAiTone.Formal,
  friendly: MailAiTone.Friendly,
  followup: MailAiTone.Followup,
};

export const toneEnum = (tone: MailToneKey): MailAiTone => TONE_ENUM[tone];

export type MailRewriteKey =
  | 'rewrite'
  | 'shorten'
  | 'formal'
  | 'friendly'
  | 'translate_pl'
  | 'translate_en'
  | 'fix';

export const MAIL_REWRITE_MODES: MailRewriteKey[] = [
  'rewrite',
  'shorten',
  'formal',
  'friendly',
  'translate_pl',
  'translate_en',
  'fix',
];

const REWRITE_ENUM: Record<MailRewriteKey, MailRewriteMode> = {
  rewrite: MailRewriteMode.Rewrite,
  shorten: MailRewriteMode.Shorten,
  formal: MailRewriteMode.Formal,
  friendly: MailRewriteMode.Friendly,
  translate_pl: MailRewriteMode.TranslatePl,
  translate_en: MailRewriteMode.TranslateEn,
  fix: MailRewriteMode.Fix,
};

export const rewriteEnum = (mode: MailRewriteKey): MailRewriteMode =>
  REWRITE_ENUM[mode];

/** Действие разбора → ключ кнопки «Что сделать». */
export type MailInsightAction =
  | 'reply'
  | 'task'
  | 'archive'
  | 'forward'
  | 'wait'
  | 'snooze'
  | 'remind';

export const insightAction = (action?: string | null): MailInsightAction => {
  const value = (action || '').toLowerCase();
  return ([
    'reply',
    'task',
    'archive',
    'forward',
    'wait',
    'snooze',
    'remind',
  ] as const).includes(value as MailInsightAction)
    ? (value as MailInsightAction)
    : 'wait';
};

/** Тон факта «Кто пишет»: ok | warn | bad, иначе нейтральный. */
export const factTone = (
  tone?: string | null,
): 'ok' | 'warn' | 'bad' | null => {
  const value = (tone || '').toLowerCase();
  return value === 'ok' || value === 'warn' || value === 'bad' ? value : null;
};

/**
 * Значения фактов, которые сервер отдаёт кодами (`active`, `blocked`,
 * `yes`, `no`, …): переводятся по `mail.sender.values.<код>`; прочие
 * (название группы, сумма) — как есть.
 */
export const FACT_VALUE_CODES = [
  'active',
  'blocked',
  'suspended',
  'yes',
  'no',
  'signed',
  'sent',
  'viewed',
  'draft',
  'declined',
  'expired',
  'cancelled',
] as const;

export const factValueKey = (value?: string | null): string | null => {
  const code = (value || '').trim().toLowerCase();
  return (FACT_VALUE_CODES as readonly string[]).includes(code) ? code : null;
};

/** Отвечает ли черновик на вопрос (сравнение без регистра и пробелов). */
export const isAnswered = (
  question: string,
  answered: readonly string[],
): boolean => {
  const norm = (text: string) => text.trim().toLowerCase().replace(/\s+/g, ' ');
  const target = norm(question);
  return answered.some((item) => norm(item) === target);
};

export const confidencePercent = (value?: number | null): number | null =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.round(Math.min(Math.max(value, 0), 1) * 100)
    : null;

/** Срок: «25.10», а не в этом году — «25.10.2027». */
export const deadlineLabel = (
  date?: string | null,
  now: moment.Moment = moment(),
): string => {
  const value = moment(date || '');
  if (!value.isValid()) return '';
  return value.isSame(now, 'year')
    ? value.format('DD.MM')
    : value.format('DD.MM.YYYY');
};
